/**
 * Live pricing for "Customise Your Ring".
 *
 * A browser port of calculate_price_madeforyou() from the WooCommerce theme.
 * Unlike the original this does no AJAX -- the rate matrices are rendered into
 * the page by ring-configurator.liquid, so the price updates on every keystroke
 * with zero network latency.
 *
 * The engine here intentionally mirrors src/engine/pricing.js. If you change a
 * formula, change it in both places and re-run `npm test`.
 */

(function (global) {
  'use strict';

  // ---------------------------------------------------------------------
  // Band specifications, lifted from calculate_price_madeforyou().
  // Net gold weight in grams, keyed by karat.
  // ---------------------------------------------------------------------
  var BANDS = {
    Plain: {
      netWeight: { 14: 2.34, 18: 2.8 },
      melee: [],
    },
    Pave: {
      netWeight: { 14: 2.34, 18: 2.8 },
      melee: [
        { pieces: 16, totalWeight: 0.06 },
        { pieces: 18, totalWeight: 0.18 },
      ],
    },
    Regal: {
      netWeight: { 14: 3.45, 18: 4.12 },
      melee: [
        { pieces: 20, totalWeight: 0.05 },
        { pieces: 2, totalWeight: 0.007 },
        { pieces: 2, totalWeight: 0.01 },
        { pieces: 6, totalWeight: 0.049 },
        { pieces: 2, totalWeight: 0.021 },
        { pieces: 2, totalWeight: 0.013 },
      ],
    },
  };

  // Melee on the band follows the centre stone's colour family. The matrix
  // grades sub-1ct stones as DEF or GH only, both at VVS-VS clarity.
  var MELEE_CLARITY = 'VVS-VS';
  function meleeColourFor(centreColour) {
    return /^[GH]/i.test(centreColour) ? 'GH' : 'DEF';
  }

  var SIZE_STEP_GRAMS = { 14: 0.07, _default: 0.08 };

  function parseKarat(v) {
    var m = String(v == null ? '' : v).match(/(\d+(?:\.\d+)?)/);
    return m ? Number(m[1]) : null;
  }

  function normClarity(v) {
    var s = String(v == null ? '' : v).trim().toUpperCase().replace(/\s+/g, '');
    return s === 'VS-VVS' || s === 'VVS-VS' ? 'VVS-VS' : s;
  }

  function normColour(v) {
    return String(v == null ? '' : v).trim().toUpperCase();
  }

  function effectiveRate(row, useDiscounted) {
    if (!useDiscounted) return Number(row.rate_per_carat) || 0;
    if (row.discounted_rate != null && row.discounted_rate !== '') return Number(row.discounted_rate);
    var pct = Number(row.discount_pct) || 0;
    return (Number(row.rate_per_carat) || 0) * (1 - pct);
  }

  /**
   * Same contiguous-band rule as the Node engine: the sheet leaves hairline
   * gaps between bands (0.009 -> 0.01) and real melee lands in them.
   */
  function pickRate(perUnit, matrix, colour, clarity, useDiscounted) {
    var candidates = matrix.filter(function (r) {
      return normColour(r.color) === colour && normClarity(r.clarity) === clarity;
    });
    if (!candidates.length) return null;

    var exact = candidates.filter(function (r) {
      return perUnit >= Number(r.carat_min) && perUnit <= Number(r.carat_max);
    })[0];
    if (exact) return effectiveRate(exact, useDiscounted);

    var sorted = candidates.slice().sort(function (a, b) {
      return Number(a.carat_min) - Number(b.carat_min);
    });
    var ceiling = Math.max.apply(
      null,
      sorted.map(function (r) { return Number(r.carat_max); })
    );
    if (perUnit > ceiling) return null;

    var winner = null;
    for (var i = 0; i < sorted.length; i++) {
      if (Number(sorted[i].carat_min) <= perUnit) winner = sorted[i];
      else break;
    }
    return winner ? effectiveRate(winner, useDiscounted) : null;
  }

  /**
   * @param {object} sel  { shape, caratSize, colour, clarity, metal, purity, bandStyle, ringSize }
   * @param {object} rates { settings, diamond_rates }
   * @returns {object} breakdown, or { error } when a grade cannot be priced
   */
  function calculate(sel, rates) {
    var settings = rates.settings || {};
    var useDiscounted = settings.use_discounted_rates !== false;

    var karat = parseKarat(sel.purity) || 14;
    var band = BANDS[sel.bandStyle] || BANDS.Plain;

    // --- centre stone --------------------------------------------------
    var carat = Number(sel.caratSize) || 0;
    var centreColour = normColour(sel.colour);
    var centreClarity = normClarity(sel.clarity);
    var centreRate = pickRate(carat, rates.diamond_rates, centreColour, centreClarity, useDiscounted);

    if (centreRate === null) {
      return {
        error:
          'No rate for a ' + carat + 'ct ' + centreColour + ' ' + centreClarity + ' stone. ' +
          'Stones under 1ct are graded DEF or GH; from 1ct up they are graded D, E, F or G.',
      };
    }
    var centrePrice = carat * centreRate;

    // --- gold ----------------------------------------------------------
    var netWeight = band.netWeight[karat] != null ? band.netWeight[karat] : band.netWeight[14];
    var size = sel.ringSize === 'Free Sizer' ? null : Number(sel.ringSize);
    var step = SIZE_STEP_GRAMS[karat] != null ? SIZE_STEP_GRAMS[karat] : SIZE_STEP_GRAMS._default;
    var extraWeight = size && size > 10 ? (size - 10) * step : 0;
    var totalWeight = netWeight + extraWeight;

    var multiplier = Number((settings.karat_multipliers || {})[String(karat)]);
    if (!multiplier) return { error: 'No gold multiplier configured for ' + karat + 'Kt' };

    var goldPrice =
      totalWeight * multiplier * Number(settings.gold_rate_24k || 0) *
      (1 + Number(settings.gold_wastage_pct || 0));

    // --- band melee ----------------------------------------------------
    var meleeColour = meleeColourFor(centreColour);
    var meleePrice = 0;
    for (var i = 0; i < band.melee.length; i++) {
      var line = band.melee[i];
      var perUnit = line.totalWeight / line.pieces;
      var rate = pickRate(perUnit, rates.diamond_rates, meleeColour, MELEE_CLARITY, useDiscounted);
      if (rate === null) continue; // band melee is a rounding-level cost; skip rather than block the sale
      meleePrice += line.totalWeight * rate;
    }

    // --- making --------------------------------------------------------
    var category = 'Diamond + Gold Jewellery';
    var makingRate = Number((settings.making_rates || {})[category]) || 0;
    var making = totalWeight * makingRate;

    // --- totals --------------------------------------------------------
    var subtotal = centrePrice + goldPrice + meleePrice + making;
    var gstRate = Number(settings.gst_rate || 0) / 100;
    var gst = subtotal * gstRate;
    var total = subtotal + gst;

    return {
      centreStone: Math.round(centrePrice),
      gold: Math.round(goldPrice),
      bandDiamonds: Math.round(meleePrice),
      making: Math.round(making),
      subtotal: Math.round(subtotal),
      gst: Math.round(gst),
      total: Math.round(total),
      weights: { net: netWeight, sizeExtra: Math.round(extraWeight * 1000) / 1000, total: Math.round(totalWeight * 1000) / 1000 },
    };
  }

  /** Colour + clarity vocabularies flip at 1ct. Mirrors the WooCommerce page. */
  function gradeOptionsFor(carat) {
    return Number(carat) < 1
      ? { colours: ['DEF', 'GH'], clarities: ['VVS-VS'] }
      : { colours: ['D', 'E', 'F', 'G'], clarities: ['VS', 'VVS'] };
  }

  function formatINR(paise) {
    return '₹' + Number(paise).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  global.CKRingConfigurator = {
    BANDS: BANDS,
    calculate: calculate,
    gradeOptionsFor: gradeOptionsFor,
    formatINR: formatINR,
    _internal: { pickRate: pickRate, parseKarat: parseKarat, normClarity: normClarity },
  };
})(typeof window !== 'undefined' ? window : globalThis);
